import type { ObjectSchema, ValidationError } from "yup";

import type { Client, Horse, Stable, Trim } from "@/types";

import { clientSchema } from "./clientSchema";
import { horseSchema } from "./horseSchema";
import { stableSchema } from "./stableSchema";
import { trimSchema } from "./trimSchema";

export type Document = Client | Horse | Stable | Trim;

type DocumentType = "client" | "horse" | "trim";

const schemaRegistry: Record<DocumentType, ObjectSchema<Document>> = {
  client: clientSchema as ObjectSchema<Document>,
  horse: horseSchema as ObjectSchema<Document>,
  stable: stableSchema as ObjectSchema<Document>,
  trim: trimSchema as ObjectSchema<Document>,
};

interface RawDocumentShape {
  _id: string;
  type: string;
}

export class DocumentValidationError extends Error {
  public readonly documentId: string;
  public readonly documentType: string;
  public readonly validationError: ValidationError;

  constructor(
    documentId: string,
    documentType: string,
    validationError: ValidationError,
  ) {
    super(
      `Validation failed for ${documentType} document "${documentId}": ${validationError.message}`,
    );
    this.name = "DocumentValidationError";
    this.documentId = documentId;
    this.documentType = documentType;
    this.validationError = validationError;
  }
}

function getSchema(type: string): ObjectSchema<Document> | undefined {
  return schemaRegistry[type as DocumentType];
}

/**
 * Hydrates a raw document from CouchDB/JSON by applying schema transformations.
 * This converts date strings to Date objects and applies default values.
 *
 * @param rawDoc - The raw document (untyped, will be validated)
 * @returns The hydrated document with proper Date objects
 * @throws DocumentValidationError if the document fails validation
 */
export function hydrateDocument<T extends Document>(rawDoc: unknown): T {
  const doc = rawDoc as RawDocumentShape;
  const schema = getSchema(doc.type);

  if (!schema) {
    throw new Error(`Unknown document type: ${doc.type}`);
  }

  try {
    return schema.validateSync(rawDoc, {
      stripUnknown: false,
      abortEarly: false,
    }) as T;
  } catch (error) {
    throw new DocumentValidationError(
      doc._id,
      doc.type,
      error as ValidationError,
    );
  }
}

/**
 * Validates a document before storing it in CouchDB.
 * Ensures all required fields are present and valid.
 *
 * @param doc - The document to validate
 * @returns The validated document (with defaults applied)
 * @throws DocumentValidationError if validation fails
 */
export function validateDocument<T extends Document>(doc: T): T {
  const schema = getSchema(doc.type);

  if (!schema) {
    throw new Error(`Unknown document type: ${doc.type}`);
  }

  try {
    return schema.validateSync(doc, {
      stripUnknown: false,
      abortEarly: false,
    }) as T;
  } catch (error) {
    throw new DocumentValidationError(
      doc._id,
      doc.type,
      error as ValidationError,
    );
  }
}
